/* nb-shell-cart-drawer.js
   Renders the cart drawer from /cart.js. Never a literal price, always a live fetch,
   and always formatted through the shop's own money_format (read from a data attribute
   on the drawer root, set by Liquid from `shop.money_format`), so SG ($) and MY (RM)
   both render correctly. Never a price inside a button: the Checkout control carries no
   amount, the live total sits in its own row above it.

   Opens on `cart:refresh` (dispatched by any add-to-cart control after a successful
   /cart/add.js call) and repaints on every quantity change, remove, and offer add.
   Uses the nb-ui.js `drawer` module (data-nb-drawer) for open/close/focus-trap/scrim;
   this file only owns fetching cart state and writing it into the DOM. */
(function () {
  // Every cart instance on the page (the drawer, and/or the /cart page panel) carries
  // data-nb-cart-root. One fetch of /cart.js paints all of them, so the drawer and the
  // page never drift out of sync when both are present.
  function roots() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-nb-cart-root]'));
  }

  function moneyFormat(el) {
    var format = el && el.dataset.moneyFormat;
    // No currency symbol is guessed. `shop.money_format` is always passed in from Liquid
    // (see the section's `data-money-format` attribute); if it is somehow missing, show
    // the bare amount rather than a wrong or assumed currency mark. Blank beats guessed.
    return format || '{{amount_no_decimals}}';
  }

  function money(cents, el) {
    // Whole units, matching Liquid's money_without_trailing_zeros, wrapped in the shop's
    // own money_format template so the currency symbol and its position are always correct.
    var value = Math.round(cents / 100);
    var withComma = value.toLocaleString('en-US');
    return moneyFormat(el)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, withComma)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withComma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, value)
      .replace(/\{\{\s*amount\s*\}\}/, value);
  }

  function eligibleProductIds(el) {
    var raw = (el && el.dataset.eligibleProducts) || '';
    return raw.split(',').filter(Boolean).map(Number);
  }

  function warrantyProductId(el) {
    var raw = el && el.dataset.warrantyProductId;
    return raw ? Number(raw) : null;
  }

  function renderEmpty(el) {
    // Empty state, both the drawer and the /cart page (same markup, shared snippet):
    // the sleeping-bottle illustration frozen on Paper board UGM-0 (file
    // 01M2M4YEG7NTF94XKNWGAFCQTZ), static (no motion pass), "Your cart is empty." and a
    // "Shop Clean Max" button. Board carried no literal copy beyond this line, so the
    // button text/target is the job brief's own fallback.
    var img = el.getAttribute('data-nb-cart-illustration');
    var wrap = document.createElement('div');
    wrap.className = 'nb-cart-page__empty';
    if (img) {
      var imgEl = document.createElement('img');
      imgEl.src = img;
      imgEl.alt = '';
      imgEl.width = 160;
      imgEl.height = 155;
      wrap.appendChild(imgEl);
    }
    var p = document.createElement('p');
    p.className = 'text-body font-sans text-ink font-medium';
    p.textContent = 'Your cart is empty.';
    wrap.appendChild(p);
    var root = el.closest('[data-nb-cart-root]');
    var tpl = root && root.querySelector('[data-nb-cart-empty-cta-template]');
    if (tpl) wrap.appendChild(tpl.content.cloneNode(true));
    el.innerHTML = '';
    el.appendChild(wrap);
  }

  // Cart contents line, per handle. Card copy already used on the site (see
  // templates/collection.nb-refills.json splitpack blocks and the Clean Max bundle
  // compare table, templates/product.nb-clean-max.json): /cart.js does not expose bundle
  // components, so this reuses that same wording rather than typing new copy. Only the
  // bonus/refill contents are counted, matching how those cards count boxes (the base
  // machine's own tablets/filter are never listed here).
  var BUNDLE_CONTENTS = {
    // Clean365 / Clean730: exact strings from the Clean Max buy box option cards,
    // sections/nb-cmax-buy.liquid checklist_12 / checklist_24 (all-boxes count).
    'clean-max-365-bundle': '6 boxes of washing tablets, 2 boxes of HEPA H13 filters, 1 box of descaler',
    'clean-max-730-bundle': '12 boxes of washing tablets, 4 boxes of HEPA H13 filters, 2 boxes of descaler',
    '3-wash-hepa-descale-pack-clean-max': '3 boxes of washing tablets, 1 box of HEPA H13 filters, 1 box of descaler',
    '3-wash-hepa-descale-pack-clean': '3 boxes of washing tablets, 1 box of HEPA H13 filters, 1 box of descaler'
  };

  // Per-handle product JSON cache (variants carry inventory_quantity/inventory_policy,
  // which /cart.js line items do not expose), so a repaint never re-fetches a handle
  // it already has.
  var productCache = {};
  function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    return productCache[handle];
  }

  // Preorder: the variant is purchasable but its inventory is at or below zero on a
  // continue selling policy. This is how preorder works on this store.
  function isPreorderVariant(product, variantId) {
    if (!product || !Array.isArray(product.variants)) return false;
    var variant = product.variants.filter(function (v) { return v.id === variantId; })[0];
    if (!variant) return false;
    return variant.inventory_quantity <= 0 && variant.inventory_policy === 'continue';
  }

  // Handles forced into "Preorder" by the section's `preorder_handles` setting, while
  // Clean Max hasn't shipped and its bundle inventory reads as in stock. Clear the
  // setting once stock ships; the inventory_quantity/continue-policy rule above then
  // takes over on its own as the fallback.
  function preorderHandles(el) {
    var raw = (el && el.dataset.preorderHandles) || '';
    return raw.split(',').map(function (h) { return h.trim(); }).filter(Boolean);
  }

  // Card-links job, 2026-09-30: a cart line's image/title area navigates to the real
  // product page with that variant/option preselected, same destinations as the site's
  // bundle/product cards (specs/GO-LIVE-FIX-BRIEF.md). Bundle handles route to the Clean
  // Max page with the matching option; the accessory pack routes to the washing tablets
  // refill picker; everything else is its own product page with ?variant=.
  function lineHref(line) {
    var handle = line.handle || '';
    if (handle === 'clean-max-365-bundle') return '/products/nuviebaby-clean-max?option=m12';
    if (handle === 'clean-max-730-bundle') return '/products/nuviebaby-clean-max?option=m24';
    if (handle === '3-wash-hepa-descale-pack-clean-max' || handle === '3-wash-hepa-descale-pack-clean') {
      return '/products/super-clean-washing-tablets?option=pack';
    }
    if (handle === 'nuviebaby-clean-max') return '/products/nuviebaby-clean-max?option=none';
    if (!handle) return '';
    return '/products/' + handle + (line.variant_id ? '?variant=' + line.variant_id : '');
  }

  function lineRow(line, root) {
    var img = line.image ? 'background-image:url(' + line.image.replace(/\.(jpg|jpeg|png|webp|gif)(\?|$)/i, '_200x200.$1$2') + ')' : '';
    var props = line.properties && line.properties._details ? line.properties._details : line.variant_title && line.variant_title !== 'Default Title' ? line.variant_title : '';
    var contents = BUNDLE_CONTENTS[line.handle] || '';
    var href = lineHref(line);
    var row = document.createElement('div');
    row.className = 'nb-cart-line';
    row.setAttribute('data-nb-cart-line', line.key);
    if (href) {
      row.setAttribute('data-nb-card-href', href);
      row.style.cursor = 'pointer';
    }
    row.innerHTML =
      '<div class="nb-cart-line__thumb" style="' + img + '"></div>' +
      '<div class="flex flex-col grow gap-[6px]">' +
        '<div class="flex justify-between gap-[12px]">' +
          '<span class="text-body leading-[21px] font-sans font-medium text-ink">' + line.product_title + '</span>' +
          '<span class="text-body leading-[21px] font-sans font-medium text-ink">' + money(line.final_line_price, root) + '</span>' +
        '</div>' +
        (props ? '<span class="text-meta leading-[18px] font-sans text-mute">' + props + '</span>' : '') +
        (contents ? '<span class="text-meta leading-[18px] font-sans text-mute">' + contents + '</span>' : '') +
        '<span class="text-meta leading-[18px] font-sans font-medium text-cobalt hidden" data-nb-cart-preorder>Preorder</span>' +
        '<div class="flex items-center justify-between pt-[6px]">' +
          '<div class="nb-cart-line__qty">' +
            '<button type="button" data-nb-cart-qty-minus class="text-body font-sans text-mute" aria-label="Decrease quantity">&minus;</button>' +
            '<span class="w-[28px] text-center text-caption font-sans font-medium text-ink" data-nb-cart-qty-value>' + line.quantity + '</span>' +
            '<button type="button" data-nb-cart-qty-plus class="text-body font-sans text-ink" aria-label="Increase quantity">+</button>' +
          '</div>' +
          '<button type="button" data-nb-cart-remove class="text-meta font-sans text-mute" style="text-decoration:underline;">Remove</button>' +
        '</div>' +
      '</div>';
    return row;
  }

  // Fetches each unique handle in the cart once, then reveals the "Preorder" note on
  // any line whose variant is available but has no stock on a continue policy.
  function paintPreorderFlags(items, linesEl, root) {
    var forced = preorderHandles(root);
    var handles = [];
    items.forEach(function (line) {
      if (line.handle && handles.indexOf(line.handle) === -1) handles.push(line.handle);
    });
    handles.forEach(function (handle) {
      var isForced = forced.indexOf(handle) !== -1;
      var lookup = isForced ? Promise.resolve(null) : fetchProduct(handle);
      lookup.then(function (product) {
        items.forEach(function (line) {
          if (line.handle !== handle) return;
          var row = linesEl.querySelector('[data-nb-cart-line="' + line.key + '"]');
          var note = row && row.querySelector('[data-nb-cart-preorder]');
          var show = isForced || isPreorderVariant(product, line.variant_id);
          if (note) note.classList.toggle('hidden', !show);
        });
      });
    });
  }

  function updateCartCount(count) {
    document.querySelectorAll('[data-nb-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.classList.toggle('hidden', count === 0);
    });
  }

  function paintOne(cart, el) {
    if (!cart || !Array.isArray(cart.items)) return; // an error reply (rate limit) has no items
    var itemCountEl = el.querySelector('[data-nb-cart-item-count]');
    var linesEl = el.querySelector('[data-nb-cart-lines]');
    var offerEl = el.querySelector('[data-nb-cart-offer]');
    var subtotalEl = el.querySelector('[data-nb-cart-subtotal]');
    var totalEl = el.querySelector('[data-nb-cart-total]');
    var footerEl = el.querySelector('[data-nb-cart-footer]');
    var empty = cart.item_count === 0;

    // Empty cart (page and drawer, per PM ruling): hide Subtotal/Delivery/Total,
    // Checkout and the trust line entirely, not just zero them out.
    if (footerEl) footerEl.hidden = empty;

    if (itemCountEl) itemCountEl.textContent = cart.item_count + (cart.item_count === 1 ? ' item' : ' items');
    if (subtotalEl) subtotalEl.textContent = money(cart.total_price, el);
    if (totalEl) totalEl.textContent = money(cart.total_price, el);

    if (linesEl) {
      linesEl.innerHTML = '';
      if (cart.item_count === 0) {
        renderEmpty(linesEl);
      } else {
        cart.items.forEach(function (line) {
          linesEl.appendChild(lineRow(line, el));
        });
        paintPreorderFlags(cart.items, linesEl, el);
      }
    }

    // Rule 2 (upsell-cross-sell-strategy.md, priority #2): a machine or bundle in the
    // cart with no 2nd year warranty. Eligibility and the warranty product are both
    // decided from explicit product ids (the `eligible_products` and `warranty_product`
    // settings), never a title match. Rules 1, 3, 4 of that doc are not built here; see
    // the section comment and this component's handback.
    if (offerEl) {
      var offerButton = offerEl.querySelector('[data-nb-cart-offer-add]');
      var eligibleIds = eligibleProductIds(el);
      var warrantyId = warrantyProductId(el);
      var hasWarranty = warrantyId != null && cart.items.some(function (line) {
        return line.product_id === warrantyId;
      });
      var hasEligibleProduct = eligibleIds.length > 0 && cart.items.some(function (line) {
        return eligibleIds.indexOf(line.product_id) !== -1;
      });
      var show = offerButton && hasEligibleProduct && !hasWarranty;
      offerEl.hidden = !show;
    }
  }

  function paint(cart) {
    updateCartCount(cart && Array.isArray(cart.items) ? cart.item_count : 0);
    roots().forEach(function (el) {
      paintOne(cart, el);
    });
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  function refresh() {
    return fetchCart().then(paint);
  }

  function refreshAndOpen() {
    return refresh().then(function () {
      var trigger = document.querySelector('[data-nb-cart-auto-open]');
      if (trigger) trigger.click();
    });
  }

  function changeItem(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (r) { return r.json(); }).then(paint);
  }

  function addVariant(variantId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (err) { throw err; });
      return r.json();
    }).then(function () {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }).catch(function (err) {
      // eslint-disable-next-line no-console
      console.error('nb-shell-cart-drawer: add failed', err);
    });
  }

  function bindLineEvents(el) {
    if (el.hasAttribute('data-nb-cart-lines-bound')) return;
    el.setAttribute('data-nb-cart-lines-bound', '');
    el.addEventListener('click', function (e) {
      var row = e.target.closest('[data-nb-cart-line]');
      if (!row) return;
      var key = row.getAttribute('data-nb-cart-line');
      var qtyEl = row.querySelector('[data-nb-cart-qty-value]');
      var qty = qtyEl ? parseInt(qtyEl.textContent, 10) : 1;
      if (e.target.closest('[data-nb-cart-qty-minus]')) {
        changeItem(key, Math.max(0, qty - 1));
      } else if (e.target.closest('[data-nb-cart-qty-plus]')) {
        changeItem(key, qty + 1);
      } else if (e.target.closest('[data-nb-cart-remove]')) {
        changeItem(key, 0);
      }
    });
  }

  function bindOfferEvents(el) {
    if (el.hasAttribute('data-nb-cart-offer-bound')) return;
    el.setAttribute('data-nb-cart-offer-bound', '');
    el.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-nb-cart-offer-add]');
      if (!btn) return;
      var variantId = btn.getAttribute('data-variant-id');
      if (variantId) addVariant(variantId);
    });
  }

  function init() {
    var found = false;
    roots().forEach(function (el) {
      if (el.hasAttribute('data-nb-cart-drawer-init')) return;
      el.setAttribute('data-nb-cart-drawer-init', '');
      found = true;
      var linesEl = el.querySelector('[data-nb-cart-lines]');
      var offerEl = el.querySelector('[data-nb-cart-offer]');
      if (linesEl) bindLineEvents(linesEl);
      if (offerEl) bindOfferEvents(offerEl);
    });
    if (found) refresh();
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('cart:refresh', refreshAndOpen);
})();
